import { call, put, takeLatest, cancelled, select } from "redux-saga/effects";
import { fetchWorkflowHistoriesRequest, fetchWorkflowHistoriesSuccess, fetchWorkflowHistoriesFailure, fetchWorkflowHistoriesDiscarded } from "../slices/workflows/workflowHistoriesSlice";
import { getWorkflowsList, getPendingTasks, searchPendingTasks, getUnassignedTasks, searchUnassignedTasks, getMyProcesses, searchMyProcesses } from "../../api/workflowsApi";
import { fetchPendingTasksRequest, fetchPendingTasksSuccess, fetchPendingTasksFailure, fetchPendingTasksDiscarded } from "../slices/workflows/pendingTasksSlice";
import { fetchUnassignedTasksRequest, fetchUnassignedTasksSuccess, fetchUnassignedTasksFailure, fetchUnassignedTasksDiscarded } from "../slices/workflows/unassignedTasksSlice";
import { fetchworkflowListRequest, fetchworkflowListSuccess, fetchworkflowListFailure } from "../slices/workflows/workflowListSlice";
import { logger } from "helpers/logger";
import { mapPendingTaskResponse, mapMyProcessResponse } from "../../dto/workflowDto";

const selectCompanyId = (state) => state.selectedCompany.data?.companyData?.companyId;

// Workflow history, "My processes". action.payload: { request, needsLabels }. request is a
// MyProcessesRequest built with createMyProcessesRequest (dto/workflowDto); needsLabels picks the
// search endpoint (search text or a label sort), otherwise the plain list endpoint. Same flow as
// getPendingTasksSaga.
function* getMyProcessesSaga(action) {
	const { request, needsLabels } = action.payload;
	const abortController = new AbortController();

	// The backend limits rows to workflows the user can see in the selected company, so it needs the company.
	const companyId = yield select(selectCompanyId);

	try {
		logger.info("🚀 Fetching my processes");

		// Only the search endpoint needs the language (it matches/sorts the translated labels).
		let response;
		if (needsLabels) {
			const lang = yield select((state) => state.translations.currentLanguage);
			response = yield call(searchMyProcesses, { ...request, companyId, lang }, abortController.signal);
		} else {
			response = yield call(getMyProcesses, { ...request, companyId }, abortController.signal);
		}

		// The user switched company while this was in flight: these are the old company's processes.
		if ((yield select(selectCompanyId)) !== companyId) {
			logger.info("🚫 My processes response discarded: the company changed while it was loading");
			yield put(fetchWorkflowHistoriesDiscarded());
			return;
		}

		logger.info("✅ Successfully fetched my processes");
		const processes = (response?.data?.processes || []).map(mapMyProcessResponse);
		yield put(fetchWorkflowHistoriesSuccess({ ...response, data: { ...response?.data, processes } }));
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled for my processes");
			return;
		}
		// An old company's error isn't this company's problem either.
		if ((yield select(selectCompanyId)) !== companyId) {
			logger.info("🚫 My processes error discarded: the company changed while it was loading");
			yield put(fetchWorkflowHistoriesDiscarded());
			return;
		}
		logger.error("❌ Failed to fetch my processes:", error);
		yield put(fetchWorkflowHistoriesFailure(error.message));
	} finally {
		if (yield cancelled()) {
			logger.info("🛑 Saga cancelled for my processes, aborting request");
			abortController.abort();
		}
	}
}

// action.payload: { request, needsLabels }. request is a PendingTasksRequest built with
// createPendingTasksRequest (dto/workflowDto); needsLabels picks the search endpoint (search text
// or a label sort), otherwise the plain list endpoint.
function* getPendingTasksSaga(action) {
	const { request, needsLabels } = action.payload;

	// ✅ Create AbortController
	const abortController = new AbortController();

	// Always the backend endpoint (never the generic /api/Supabase/all): the backend limits rows to
	// workflows the user can see in the selected company, so it needs the company.
	const companyId = yield select(selectCompanyId);

	try {
		logger.info("🚀 Fetching pending tasks");

		// Only the search endpoint needs the language (it matches/sorts the translated labels);
		// the plain list returns keys and the table translates them.
		let response;
		if (needsLabels) {
			const lang = yield select((state) => state.translations.currentLanguage);
			response = yield call(searchPendingTasks, { ...request, companyId, lang }, abortController.signal);
		} else {
			response = yield call(getPendingTasks, { ...request, companyId }, abortController.signal);
		}

		// The user switched company while this was in flight: these are the old company's tasks.
		if ((yield select(selectCompanyId)) !== companyId) {
			logger.info("🚫 Pending tasks response discarded: the company changed while it was loading");
			yield put(fetchPendingTasksDiscarded());
			return;
		}

		logger.info(`✅ Successfully fetched pending tasks`);
		const tasks = (response?.data?.tasks || []).map(mapPendingTaskResponse);
		yield put(fetchPendingTasksSuccess({ ...response, data: { ...response?.data, tasks } }));
	} catch (error) {
		// ✅ Check if error is from abort
		if (error.name === "AbortError") {
			logger.info(`🚫 Request cancelled for pending tasks`);
			return; // Don't dispatch failure for cancelled requests
		}
		// An old company's error isn't this company's problem either.
		if ((yield select(selectCompanyId)) !== companyId) {
			logger.info("🚫 Pending tasks error discarded: the company changed while it was loading");
			yield put(fetchPendingTasksDiscarded());
			return;
		}
		logger.error(`❌ Failed to fetch pending tasks:`, error);
		yield put(fetchPendingTasksFailure(error.message));
	} finally {
		// ✅ Abort if saga is cancelled by takeLatest
		if (yield cancelled()) {
			logger.info(`🛑 Saga cancelled for pending tasks, aborting request`);
			abortController.abort();
		}
	}
}

// Unassigned tasks. action.payload: { request, needsLabels }, the same as getPendingTasksSaga
// (PendingTasksRequest; needsLabels picks the search endpoint). Rows have the pending-task shape.
function* getUnassignedTasksSaga(action) {
	const { request, needsLabels } = action.payload;
	const abortController = new AbortController();

	// The backend limits rows to the company's workflows and the user's roles there, so it needs the company.
	const companyId = yield select(selectCompanyId);

	try {
		logger.info("🚀 Fetching unassigned tasks");

		let response;
		if (needsLabels) {
			const lang = yield select((state) => state.translations.currentLanguage);
			response = yield call(searchUnassignedTasks, { ...request, companyId, lang }, abortController.signal);
		} else {
			response = yield call(getUnassignedTasks, { ...request, companyId }, abortController.signal);
		}

		// The user switched company while this was in flight: these are the old company's tasks.
		if ((yield select(selectCompanyId)) !== companyId) {
			logger.info("🚫 Unassigned tasks response discarded: the company changed while it was loading");
			yield put(fetchUnassignedTasksDiscarded());
			return;
		}

		logger.info("✅ Successfully fetched unassigned tasks");
		const tasks = (response?.data?.tasks || []).map(mapPendingTaskResponse);
		yield put(fetchUnassignedTasksSuccess({ ...response, data: { ...response?.data, tasks } }));
	} catch (error) {
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled for unassigned tasks");
			return;
		}
		// An old company's error isn't this company's problem either.
		if ((yield select(selectCompanyId)) !== companyId) {
			logger.info("🚫 Unassigned tasks error discarded: the company changed while it was loading");
			yield put(fetchUnassignedTasksDiscarded());
			return;
		}
		logger.error("❌ Failed to fetch unassigned tasks:", error);
		yield put(fetchUnassignedTasksFailure(error.message));
	} finally {
		if (yield cancelled()) {
			logger.info("🛑 Saga cancelled for unassigned tasks, aborting request");
			abortController.abort();
		}
	}
}

function* fetchWorkflowListSaga(action) {
	const { userId, companyId } = action.payload;

	// ✅ Create AbortController
	const abortController = new AbortController();

	try {
		logger.info("🚀 Fetching workflow list");

		const response = yield call(
			getWorkflowsList,
			userId,
			companyId,
			abortController.signal // ✅ Pass signal to API
		);
		logger.info("✅ Successfully fetched workflow list");
		yield put(fetchworkflowListSuccess(response));
	} catch (error) {
		// ✅ Check if error is from abort
		if (error.name === "AbortError") {
			logger.info("🚫 Request cancelled");
			return; // Don't dispatch failure for cancelled requests
		}
		logger.error("❌ Failed to fetch data", error);
		yield put(fetchworkflowListFailure(error.message));
	} finally {
		// ✅ Abort if saga is cancelled by takeLatest
		if (yield cancelled()) {
			logger.info("🛑 Saga cancelled, aborting request");
			abortController.abort();
		}
	}
}

export default function* workflowsSaga() {
	yield takeLatest(fetchWorkflowHistoriesRequest.type, getMyProcessesSaga);
	yield takeLatest(fetchPendingTasksRequest.type, getPendingTasksSaga);
	yield takeLatest(fetchUnassignedTasksRequest.type, getUnassignedTasksSaga);
	yield takeLatest(fetchworkflowListRequest.type, fetchWorkflowListSaga);
}
